import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Sparkles, Crown, Zap, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

type Tier = 'free' | 'pro' | 'quant';

interface SubscriptionRow {
  tier: Tier;
  status: string | null;
  current_period_end: string | null;
  stripe_subscription_id: string | null;
}

const TIER_META: Record<Tier, { label: string; icon: React.ComponentType<{ className?: string }>; accent: string }> = {
  free:  { label: 'Free',  icon: Zap,       accent: 'text-muted-foreground' },
  pro:   { label: 'Pro',   icon: Sparkles,  accent: 'text-primary' },
  quant: { label: 'Quant', icon: Crown,     accent: 'text-amber-500' },
};

const NEXT_STEPS: Record<Tier, string[]> = {
  free: [
    'Explore demo trading with your starter balance',
    'Try a built-in strategy on a demo account',
  ],
  pro: [
    'Connect a live broker in the Accounts hub',
    'Deploy a strategy in live execution mode',
    'Review AI-generated signals and approve trades',
  ],
  quant: [
    'Connect unlimited broker accounts',
    'Enable AI auto-execution on your strategies',
    'Publish a strategy to the marketplace',
  ],
};

const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 8;

const CheckoutSuccess: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');

  const [sub, setSub] = useState<SubscriptionRow | null>(null);
  const [polling, setPolling] = useState(true);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetchSub = async () => {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('tier, status, current_period_end, stripe_subscription_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (cancelled) return;
      if (!error && data) {
        setSub(data as SubscriptionRow);
        if ((data.tier as Tier) !== 'free') {
          setPolling(false);
          return;
        }
      }
      setPollCount((n) => {
        const next = n + 1;
        if (next >= MAX_POLLS) setPolling(false);
        return next;
      });
    };

    fetchSub();
    if (!polling) return;
    const id = setInterval(fetchSub, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, polling]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const tier: Tier = (sub?.tier as Tier) ?? 'free';
  const meta = TIER_META[tier];
  const Icon = meta.icon;
  const stillProcessing = polling && tier === 'free';
  const renewalDate = sub?.current_period_end
    ? new Date(sub.current_period_end).toLocaleDateString(undefined, { dateStyle: 'medium' })
    : null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 ring-2 ring-emerald-500/20">
            <CheckCircle2 className="h-9 w-9 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">
            {stillProcessing ? 'Confirming your subscription' : 'Subscription active'}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {stillProcessing
              ? 'Finalizing your payment. This usually takes a few seconds.'
              : 'Thanks for upgrading — your account has been updated.'}
          </p>
          {sessionId && (
            <p className="text-[11px] text-muted-foreground/70 mt-3 font-mono">
              Ref: {sessionId.slice(0, 24)}…
            </p>
          )}
        </div>

        <Card className="border-border/60 mb-8 rounded-xl">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted/50">
                  {stillProcessing ? (
                    <Loader2 className={`h-5 w-5 animate-spin ${meta.accent}`} />
                  ) : (
                    <Icon className={`h-5 w-5 ${meta.accent}`} />
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Active plan</p>
                  <p className="text-lg font-semibold">{meta.label}</p>
                </div>
              </div>
              <Badge variant={sub?.status === 'active' ? 'default' : 'secondary'}>
                {sub?.status ?? (stillProcessing ? 'Pending' : 'Active')}
              </Badge>
            </div>

            {renewalDate && (
              <p className="text-xs text-muted-foreground mt-4">
                Next renewal: <span className="text-foreground font-medium">{renewalDate}</span>
              </p>
            )}
          </CardContent>
        </Card>

        <div className="mb-8">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            What's next
          </h2>
          <ul className="space-y-2">
            {NEXT_STEPS[tier].map((step) => (
              <li key={step} className="flex items-start gap-3 text-sm">
                <ArrowRight className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button asChild className="flex-1">
            <Link to="/">Go to dashboard</Link>
          </Button>
          {tier !== 'free' && (
            <Button asChild variant="outline" className="flex-1">
              <Link to="/">Connect a broker</Link>
            </Button>
          )}
        </div>

        {stillProcessing && pollCount >= MAX_POLLS && (
          <p className="text-xs text-muted-foreground text-center mt-6">
            Still showing the previous plan? Refresh in a minute or contact support@autotradeplug.com.
          </p>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
